import classificationExternal from '@assets/img/svg/classification-empty.svg'
import rankingEmpty from '@assets/img/svg/ranking-empty.svg'
import { Campaign, defaultImage, LexLuthorService, LogService, Pagination, Ranking } from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import EmptyPageComponent from '@src/components/empty-page/empty-page.component'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation } from 'react-router'
import './ranking.page.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  userRanking: Ranking
  campaign: Campaign
}

export default ({ campaign, userRanking }: Props) => {
  const PAGE_SIZE = useMemo(() => 15, [])
  const logService = useMemo(() => new LogService(), [])
  const lexLuthorService = useMemo(() => new LexLuthorService(), [])
  const [loading, setLoading] = useState(false)
  const { search, pathname } = useLocation()
  const [page, setPage] = useState(parseInt(new URLSearchParams(search).get('page'), 10) || 0)
  const [total, setTotal] = useState(0)
  const [ranking, setRanking] = useState<{
    data: Ranking[]
    total: number
  }>({ data: [], total: 0 })
  const [t, i18n] = useTranslation('engage')
  logService.init('Engage Ranking Page')

  useEffect(() => {
    if (page < 1) {
      if (userRanking?.position) {
        setPage(Math.ceil(userRanking?.position / PAGE_SIZE))
      } else {
        setPage(1)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRanking?.position])

  useEffect(() => {
    if (page > 0 && !loading && !campaign?.activation?.hasGamificationExternal && !campaign?.activation?.disableRankingVisualization) {
      setLoading(true)
      let positionIsNotInRankingYet = false
      lexLuthorService
        .getRankings(campaign._id, '', page, PAGE_SIZE)
        .then((newRanking) => {
          positionIsNotInRankingYet = !ranking?.data?.length && page > 1
          if (total === 0) {
            setTotal(Math.ceil(newRanking.total / PAGE_SIZE))
          }
          setRanking(newRanking)
        })
        .finally(() => {
          setLoading(false)
          if (positionIsNotInRankingYet) {
            setPage(1)
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  if (campaign?.activation?.disableRankingVisualization) {
    return <Navigate to={`${pathname?.replace('ranking', 'pointing')}`} />
  }

  if (campaign?.activation?.hasGamificationExternal) {
    const link = campaign.activation.hasGamificationExternal?.replace('__USERNAME__', userRanking?.user?.nickname || '')
    return (
      <div className="engage-ranking-page">
        <EmptyPageComponent
          title={t('rankingPage.external')}
          description={t('rankingPage.externalText')}
          image={classificationExternal}
          icon={'fa-solid fa-ranking-star'}
          link={link}
          linkText={t('rankingPage.linkText')}
        />
      </div>
    )
  }

  if (!ranking?.data?.length && !loading) {
    return (
      <div className="engage-ranking-page">
        <EmptyPageComponent
          title={t('empty.title')}
          description={t('empty.description')}
          image={rankingEmpty}
          icon={'fa-solid fa-ranking-star'}
        />
      </div>
    )
  }

  return (
    <div className="engage-ranking-page">
      {loading ? (
        <div
          className="text-center my-5 display-flex align-items-center justify-content-center"
          style={{ width: '100%', height: '1000px' }}
        >
          <SqLoader size="big" />
        </div>
      ) : (
        <ul>
          {ranking?.data?.map((r, i) => (
            <li key={r?.user?.idProfile} className={`${userRanking?.user?.idProfile === r?.user?.idProfile ? 'active' : ''}`}>
              <div className="ranking-position" title={`Position ${r?.position}`}>
                <span>#{r?.position}</span>
              </div>
              <figure className="ranking-user">
                <img
                  src={`https://storage.googleapis.com/squid-profile-pictures/${r?.user?.idProfile}.jpg`}
                  className="img-fluid"
                  title={r?.user?.nickname || 'User Avatar'}
                  alt={r?.user?.nickname || 'User Avatar'}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null
                    currentTarget.src = defaultImage
                  }}
                />
              </figure>
              <div className="ranking-username">
                <span title={r?.user?.nickname || ''}>{r?.user?.nickname || ''}</span>
              </div>
              <div className="ranking-points">
                <span title={r?.total?.toLocaleString(i18n.language)}>
                  {r?.total?.toLocaleString(i18n.language)} {t('points')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <footer>
        <Pagination
          totalPages={total}
          currentPage={page}
          onPageChange={(p) => {
            p !== page ? setPage(p) : null
          }}
        />
      </footer>
    </div>
  )
}
